import { apiUrl } from "./base";

export interface InsightAPI {
  id: string;
  sessionId: string;
  type: string;
  data: any;
  createdAt: string;
  updatedAt: string;
}

export interface CreateInsightRequest {
  sessionId: string;
  type: string;
  data: any;
}

export interface InsightsResponse {
  insights: InsightAPI[];
  count: number;
}

/**
 * Get all insights for a session
 */
export async function getSessionInsights(
  sessionId: string
): Promise<InsightsResponse> {
  const response = await fetch(
    apiUrl(`/api/insights?sessionId=${encodeURIComponent(sessionId)}`)
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch insights: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Create a new insight
 */
export async function createInsight(
  data: CreateInsightRequest
): Promise<InsightAPI> {
  const response = await fetch(apiUrl("/api/insights"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to create insight: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Update an insight
 */
export async function updateInsight(
  id: string,
  data: { data: any }
): Promise<InsightAPI> {
  const response = await fetch(
    apiUrl(`/api/insights/${id}`),
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to update insight: ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Delete an insight
 */
export async function deleteInsight(id: string): Promise<void> {
  const response = await fetch(apiUrl(`/api/insights/${id}`), {
    method: "DELETE",
  });

  if (!response.ok) {
    throw new Error(`Failed to delete insight: ${response.statusText}`);
  }
}

/**
 * Save meeting insights (summary, action items, entities)
 */
export async function saveMeetingInsights(
  sessionId: string,
  insights: any
): Promise<InsightAPI> {
  return createInsight({
    sessionId,
    type: "meeting_insights",
    data: insights,
  });
}

/**
 * Save session state (questions, answers, etc.)
 */
export async function saveSessionState(
  sessionId: string,
  state: any
): Promise<InsightAPI> {
  return createInsight({
    sessionId,
    type: "session_state",
    data: state,
  });
}
